import { memo } from 'react';
import { useDateFormatter } from 'react-aria';
import {
  CalendarCell,
  CalendarGrid,
  DateRange,
  DateRangePicker as RACDateRangePicker,
  DateRangePickerProps as RACDateRangePickerProps,
  DateValue,
  Dialog,
  Group,
  Heading,
  RangeCalendar,
} from 'react-aria-components';

import { merge } from '@/components';
import { Button, ButtonProps, Popover, Text } from '@/components/core';
import { ChevronIcon } from '@/components/icons';

export type DateRangePickerProps = RACDateRangePickerProps<DateValue> & {
  label: string;
  dateRange?: DateRange | null;
  onDateRangeChange?: (value: DateRange | null) => void;
  props?: { button?: ButtonProps };
};

const IDateRangePicker = ({ label, dateRange, onDateRangeChange, props, className, ...rest }: DateRangePickerProps) => {
  const { button: buttonProps } = props ?? {};
  const formatter = useDateFormatter({ day: 'numeric', month: 'short' });
  const yearFormatter = useDateFormatter({ year: 'numeric' });

  return (
    <RACDateRangePicker aria-label={label} className={className} value={dateRange} {...rest}>
      <Group className="flex items-center">
        <Button variant="plain" {...buttonProps}>
          <Text slot="description" className="pointer-events-none">
            {dateRange?.start && dateRange?.end
              ? (() => {
                  const start = dateRange.start.toDate('UTC');
                  const end = dateRange.end.toDate('UTC');

                  if (dateRange.start.year === dateRange.end.year) {
                    return `${formatter.format(start)} - ${formatter.format(end)}, ${yearFormatter.format(end)}`;
                  } else {
                    return `${formatter.format(start)}, ${yearFormatter.format(start)} - ${formatter.format(end)}, ${yearFormatter.format(end)}`;
                  }
                })()
              : label}
          </Text>
        </Button>
      </Group>
      <Popover>
        <Dialog>
          <RangeCalendar onChange={onDateRangeChange}>
            <header className="flex items-center justify-center gap-4 p-2">
              <Button variant="plain" slot="previous">
                <ChevronIcon rotation="left" />
              </Button>
              <Heading className="text-sm font-semibold text-gray-700" />
              <Button variant="plain" slot="next">
                <ChevronIcon rotation="right" />
              </Button>
            </header>
            <CalendarGrid className="text-sm font-semibold text-gray-600" weekdayStyle="short">
              {(date) => (
                <CalendarCell
                  className={merge([
                    'cursor-pointer',
                    'data-[disabled]:bg-gray-100',
                    'data-[disabled]:text-gray-400',
                    'data-[selected]:[&:not([data-selection-start])]:[&:not([data-selection-end])]:bg-primary-50',
                    'data-[selected]:[&:not([data-selection-start])]:[&:not([data-selection-end])]:h-4',
                    'data-[selected]:[&:not([data-selection-start])]:[&:not([data-selection-end])]:my-2',
                    'data-[selected]:[&:not([data-selection-start])]:[&:not([data-selection-end])]:rounded-none',
                    'data-[selection-end]:bg-primary-500',
                    'data-[selection-end]:outline-none',
                    'data-[selection-end]:rounded-lg',
                    'data-[selection-end]:text-white',
                    'data-[selection-start]:bg-primary-500',
                    'data-[selection-start]:outline-none',
                    'data-[selection-start]:rounded-lg',
                    'data-[selection-start]:text-white',
                    'flex',
                    'items-center',
                    'justify-center',
                    'p-3',
                    'size-10',
                    'text-sm',
                  ])}
                  date={date}
                />
              )}
            </CalendarGrid>
          </RangeCalendar>
        </Dialog>
      </Popover>
    </RACDateRangePicker>
  );
};

export const DateRangePicker = memo(IDateRangePicker) as typeof IDateRangePicker;
